import { Box, Button, Typography, styled } from '@mui/material';
import { TOP_NAV, TOP_NAV_LOGIN_MOBILE } from '~/layouts/Layout/Layout.style';
import { pxToRem } from '~/theme/typography';
import { routePath } from '~/utils/constants';

type TStyledHeaderBg = {
  path: string;
};

export const StyledHeaderBg = styled(Box, {
  shouldForwardProp: prop => prop !== 'path',
})<TStyledHeaderBg>(({ theme, path }) => ({
  backgroundColor: theme.palette.primary.main,
  height: TOP_NAV,
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  position: 'fixed',
  right: 0,
  left: 0,
  zIndex: 2,
  boxShadow: '0px 0.8px 5px 3px #0000002B',
  [theme.breakpoints.down('tablet')]: {
    height: path === routePath.login ? TOP_NAV_LOGIN_MOBILE : TOP_NAV,
  },
}));

type TBottomLink = {
  isSelected: boolean;
  isWeb: boolean;
  hasManyWords: boolean;
};

export const header = {
  Container: styled(Box)(({ theme }) => ({
    padding: '0px 24px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('mobile')]: {
      padding: '0px 16px',
    },
  })),
  LogoContainer: styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    button: { display: 'none' },
    padding: 0,
    [theme.breakpoints.down('desktop')]: {
      button: { display: 'flex' },
    },
    [theme.breakpoints.down('tablet')]: {
      button: {
        padding: '12px 14px',
      },
      p: {
        display: 'none',
      },
    },
  })),
  ImageContainer: styled(Box)(({ theme }) => ({
    width: '200px',
    height: '72px',
    cursor: 'pointer',
    padding: '6px 21px',
    color: 'white',
    [theme.breakpoints.down('mobile')]: {
      width: '133px',
      marginRight: '0px',
      padding: '9px',
      display: 'flex',
    },
  })),
  Version: styled('div')(({ theme }) => ({
    paddingLeft: '5px',
    textAlign: 'justify',
    textTransform: 'none',
    fontSize: pxToRem(10),
    [theme.breakpoints.down('mobile')]: {
      display: 'none',
    },
  })),
  Image: styled('img')(({ theme }) => ({
    width: '152px',
    marginRight: '10px',
    cursor: 'pointer',
    [theme.breakpoints.down('mobile')]: {
      width: '133px',
      marginRight: '0px',
    },
  })),
  Nav: styled('nav')(({ theme }) => ({
    overflowY: 'hidden',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('desktop')]: {
      display: 'none',
    },
  })),
  Link: styled(Box)(() => ({
    width: '140px',
    padding: '20px 0px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 10px',
    '& a': {
      color: 'white',
      textDecoration: 'none',
      fontWeight: '500',
      fontSize: pxToRem(20),
      position: 'relative',
      '&>svg': {
        verticalAlign: 'sub',
        marginRight: '8px',
      },
      '&.active::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        height: '3px',
        width: 'calc(100% + 20px)',
        backgroundColor: 'white',
        borderRadius: '3px',
        left: '-10px',
        bottom: '-14px',
      },
    },
  })),
  TopNavTikuText: styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: '500',
    fontSize: pxToRem(20),
    background: 'white',
    borderRadius: '31px',
    height: '54px',
    width: '208px',
    lineHeight: '54px',
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      background: '#FBEFDC',
    },
  })),
  BottomNav: styled('nav')(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('desktop')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      position: 'fixed',
      backgroundColor: 'red',
      zIndex: '3',
      bottom: '0',
      left: '0',
      right: '0',
    },
  })),
  BottomLink: styled(Box, {
    shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'hasManyWords',
  })<TBottomLink>(({ isSelected, theme, hasManyWords }) => ({
    '& a': {
      all: 'unset',
      color: isSelected ? '#837300' : 'white',
      fontSize: pxToRem(18),
      display: 'flex',
      justifyContent: 'center',
      padding: '8px 0px',
      cursor: 'pointer',
      width: '100%',
      [theme.breakpoints.down('mobile')]: {
        fontSize: pxToRem(16),
        width: hasManyWords ? '64px' : '',
      },
    },
    backgroundColor: isSelected ? '#F6EB62' : theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: isSelected ? 'solid 5px #837300' : '',
  })),
  Login: styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: '10px',
    '& svg': {
      marginRight: '10px',
    },
  })),
  User: styled(Typography)(({ theme }) => ({
    wordBreak: 'keep-all',
    [theme.breakpoints.down('mobile')]: {
      display: 'none',
    },
  })),
  LoginBtn: styled(Button)(({ theme }) => ({
    color: theme.palette.secondary.contrastText,
    borderColor: theme.palette.secondary.contrastText,
    marginLeft: '20px',
    '&.Mui-disabled': {
      color: theme.palette.secondary.contrastText,
      borderColor: theme.palette.secondary.contrastText,
      opacity: 1,
    },
    '&:hover': {
      background: theme.palette.secondary.contrastText,
      color: theme.palette.text.secondary,
    },
    [theme.breakpoints.down('mobile')]: {
      marginLeft: '6px',
    },
  })),
  LoginTitle: styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: pxToRem(32),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('tablet')]: {
      fontSize: pxToRem(16),
    },
  })),
};
